<template>
    <prime-dialog
        v-model:visible="showConfirmation"
        modal
        :draggable="false"
        :closable="false"
        class="w-[25rem]"
        :aria-label="
            t('layout.signOutPrompt', {
                name: viewer?.firstName ?? ''
            })
        "
        :pt="{
            rejectButton: {
                outlined: true
            }
        }"
        :pt-options="{
            mergeSections: true,
            mergeProps: true
        }"
    >
        <form @submit="confirmCustomer">
            <div class="flex w-full flex-col items-center gap-3">
                <i class="pi pi-question-circle text-4xl text-primary-500"></i>
                <p class="text-center text-xl font-semibold">
                    {{
                        t('layout.signOutPrompt', {
                            name: viewer?.firstName ?? ''
                        })
                    }}
                </p>
                <p
                    v-if="requiresDateOfBirthConfirmation"
                    class="text-center"
                >
                    {{ t('layout.planPresentationDateOfBirthPrompt') }}
                </p>
                <dropdown-date-input
                    v-if="requiresDateOfBirthConfirmation"
                    :date="dateOfBirth"
                    :show-errors="showDateOfBirthError"
                    :invalid="showDateOfBirthError"
                    @update:date="dateOfBirth = $event"
                />
                <span
                    v-if="
                        requiresDateOfBirthConfirmation && showDateOfBirthError
                    "
                    class="text-red-600 text-center"
                >
                    {{ t('layout.planPresentationDateOfBirthError') }}
                </span>
            </div>

            <div class="mt-8 flex w-full flex-col gap-6">
                <prime-button
                    type="submit"
                    :label="
                        requiresDateOfBirthConfirmation
                            ? t('continue')
                            : t('layout.iAmCustomer', {
                                  name: viewer?.firstName ?? ''
                              })
                    "
                    class="w-full"
                />

                <prime-button
                    type="button"
                    :label="t('layout.notMe')"
                    outlined
                    class="w-full"
                    @click="signOut"
                />
            </div>
        </form>
    </prime-dialog>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import PrimeDialog from 'primevue/dialog'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { type RouteLocationNormalized, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import { useViewer } from '~/composables/use-login'
import type {
    StorefrontCatalogScalar,
    StorefrontNode
} from '~/resources/scalars/storefront-catalog-scalar'

const props = defineProps<{
    catalog: StorefrontCatalogScalar
}>()

const emit = defineEmits<{
    signOut: [destination: string | undefined]
}>()

const { t } = useI18n({ useScope: 'global' })
const router = useRouter()
const viewer = useViewer()
const showConfirmation = ref(false)
const dateOfBirth = ref('')
const showDateOfBirthError = ref(false)
const pendingPlanPresentationPath = ref<string>()
let confirmedPlanPresentationPath: string | undefined
const requiresDateOfBirthConfirmation = computed(() => {
    return viewer.value !== undefined && viewer.value.dateOfBirth !== null
})

const planPresentationRouteSegments = new Set([
    'agreements',
    'gift-cards',
    'memberships',
    'passes',
    'programs'
])

function isKioskRoute(route: RouteLocationNormalized): boolean {
    return (
        typeof route.params.mode === 'string' &&
        route.params.mode.startsWith('kiosk-')
    )
}

function hasStorefrontPlanSlug(node: StorefrontNode, slug: string): boolean {
    if (node.__typename === 'StorefrontPlan') {
        return node.slug === slug
    }

    return node.children.some(child => hasStorefrontPlanSlug(child, slug))
}

function isPlanPresentationRoute(route: RouteLocationNormalized): boolean {
    const routeSegments = route.path.split('/').filter(Boolean)
    const pageSegment = routeSegments.at(-2)

    if (
        pageSegment !== undefined &&
        planPresentationRouteSegments.has(pageSegment)
    ) {
        return true
    }

    // Storefront node URLs may point at a catalog, calendar, or plan, so only
    // prompt when the catalog proves this node renders a purchase presentation.
    return (
        pageSegment === 'n' &&
        hasStorefrontPlanSlug(props.catalog, routeSegments.at(-1) ?? '')
    )
}

function isSameKioskStorefront(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
): boolean {
    return (
        to.params.mode === from.params.mode &&
        to.params.facility === from.params.facility &&
        to.params.storefront === from.params.storefront
    )
}

function confirmCustomer(event: Event) {
    event.preventDefault()

    if (viewer.value === undefined) {
        return
    }

    if (
        requiresDateOfBirthConfirmation.value &&
        viewer.value.dateOfBirth !== dateOfBirth.value
    ) {
        showDateOfBirthError.value = true
        return
    }

    const destination = pendingPlanPresentationPath.value
    showConfirmation.value = false
    showDateOfBirthError.value = false
    pendingPlanPresentationPath.value = undefined

    if (destination !== undefined) {
        // Allow only the route whose DOB check just succeeded, then require a
        // new confirmation when the customer selects another presentation.
        confirmedPlanPresentationPath = destination
        router.push(destination)
    }
}

function signOut() {
    const destination = pendingPlanPresentationPath.value
    pendingPlanPresentationPath.value = undefined
    showConfirmation.value = false
    emit('signOut', destination)
}

let unregisterPlanPresentationGuard: (() => void) | undefined

onMounted(() => {
    // This component only renders inside KioskLayout. The destination check
    // still keeps the guard isolated while navigating away from a kiosk URL.
    unregisterPlanPresentationGuard = router.beforeEach((to, from) => {
        if (
            !isKioskRoute(to) ||
            !isKioskRoute(from) ||
            !isSameKioskStorefront(to, from) ||
            !isPlanPresentationRoute(to) ||
            isPlanPresentationRoute(from) ||
            viewer.value === undefined
        ) {
            return true
        }

        if (confirmedPlanPresentationPath === to.fullPath) {
            confirmedPlanPresentationPath = undefined
            return true
        }

        pendingPlanPresentationPath.value = to.fullPath
        dateOfBirth.value = ''
        showDateOfBirthError.value = false
        showConfirmation.value = true

        return false
    })
})

onUnmounted(() => {
    unregisterPlanPresentationGuard?.()
})

watch(dateOfBirth, () => {
    showDateOfBirthError.value = false
})
</script>
